import { EduleadError } from 'edulead'
import { DependencyList, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import General from '../utils/GeneralUtils'

/**
 * Generic helpers for code that calls the Go API through `eduleadV4` (edulead-v4-client.ts).
 * They know nothing about any resource: services call the SDK's per-RPC functions directly and
 * hand their errors to these.
 */

/** What a Go API error says: its message, and the ErrorInfo reason, field and metadata when present. */
interface V4Error {
  message: string
  reason?: string
  field?: string
  requestId?: string
  metadata?: Record<string, string>
}

interface ErrorInfoDetail {
  '@type'?: string
  reason?: string
  metadata?: Record<string, string>
}

/**
 * The message, reason, field, request id and ErrorInfo metadata of an SDK error. The SDK's own
 * `message` is "Status code: …\nBody: {…}", never shown to users; the API's message is in the body.
 */
export function v4Error(err: unknown): V4Error {
  if (err instanceof EduleadError && err.body && typeof err.body === 'object') {
    const body = err.body as { message?: string; details?: ErrorInfoDetail[] }
    const info = body.details?.find((d) => d['@type']?.endsWith('google.rpc.ErrorInfo'))
    return {
      message: body.message || `HTTP ${err.statusCode ?? 'error'}`,
      reason: info?.reason,
      field: info?.metadata?.field,
      requestId: info?.metadata?.request_id ?? err.requestId,
      metadata: info?.metadata,
    }
  }
  return { message: err instanceof Error ? err.message : String(err) }
}

/**
 * Logs the error and shows it as the app's other errors look (`General.logAndToastError`):
 * "<context>. <message>", with the request id for support.
 */
export function toastV4Error(err: unknown, context: string): void {
  console.error(context, err)
  const { message, requestId } = v4Error(err)
  toast.warn(`${context}. ${General.ellipsisInMiddle(message, 300)}${requestId ? ` (request ${requestId})` : ''}`)
}

/**
 * Every item of a paged list, following next-page tokens to the end. Only for lists the spec
 * bounds small; never to count a large table.
 */
export async function allPages<T>(firstPage: Promise<AsyncIterable<T>>): Promise<T[]> {
  const items: T[] = []
  for await (const item of await firstPage) {
    items.push(item)
  }
  return items
}

/**
 * The stored name of an enum value without its type prefix, as the page showed it before the Go
 * API (`enumLabel('MEMBER_ROLE_OWNER', 'MEMBER_ROLE_')` is `OWNER`); '' for unset and
 * `*_UNSPECIFIED`. The prefix is explicit: a value cannot say where its type name ends.
 */
export function enumLabel(value: string | undefined, prefix: string): string {
  if (!value || value === `${prefix}UNSPECIFIED`) return ''
  return value.startsWith(prefix) ? value.slice(prefix.length) : value
}

/** What `useTokenPager` needs of an SDK `Page`: its items and its response with the next token. */
interface TokenPage<T, R extends { next_page_token?: string }> {
  data: T[]
  response: R
}

/**
 * One page of a token-paged list at a time, for pages that show previous and next
 * (`frontend-boss/services/edulead-go.ts`'s pager, with the page's response too, for its
 * `total_size`). `fetchPage` is the SDK call for a token; `deps` changing starts again from
 * page 1. The token of every visited page is kept, so `prev` reloads a page already seen; `items`
 * is null until the first page arrives. An error is toasted with `errorContext`, or only logged
 * without one.
 */
export function useTokenPager<T, R extends { next_page_token?: string }>(
  fetchPage: (pageToken?: string) => Promise<TokenPage<T, R>>,
  deps: DependencyList,
  errorContext?: string
) {
  const [items, setItems] = useState<T[] | null>(null)
  const [response, setResponse] = useState<R | null>(null)
  const [loading, setLoading] = useState(true)
  const [tokens, setTokens] = useState<string[]>(['']) // tokens[i] loads page i + 1
  const [nextToken, setNextToken] = useState('')
  const fetchRef = useRef(fetchPage)
  fetchRef.current = fetchPage
  const requestRef = useRef(0)

  // Loads the page whose token is last in `stack`; a response to a superseded request is dropped.
  const load = async (stack: string[]) => {
    const request = ++requestRef.current
    setLoading(true)
    try {
      const page = await fetchRef.current(stack[stack.length - 1] || undefined)
      if (request !== requestRef.current) return
      setTokens(stack)
      setItems(page.data)
      setResponse(page.response)
      setNextToken(page.response.next_page_token ?? '')
    } catch (err) {
      if (request !== requestRef.current) return
      if (errorContext) {
        toastV4Error(err, errorContext)
      } else {
        console.error(err)
      }
    } finally {
      if (request === requestRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    void load([''])
  }, deps)

  return {
    items,
    response,
    loading,
    page: tokens.length,
    hasNext: nextToken !== '',
    next: () => {
      if (nextToken) void load([...tokens, nextToken])
    },
    prev: () => {
      if (tokens.length > 1) void load(tokens.slice(0, -1))
    },
    reload: () => void load(tokens),
  }
}
